import React, { useCallback } from 'react';

import { TFunction, useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import {
  AutocompleteRenderOptionState,
  Avatar,
  Box,
  Checkbox,
  Stack,
  Typography,
} from '@lyearn/ui';
import AsyncSelect from '@/components/AsyncSelect';
import { ClientFilterFragment } from '@/components/FilterManager/EntityFilter/entity/client/graphql/fragment/generated/ClientFilter';
import useClientsQuery from '@/components/FilterManager/EntityFilter/entity/client/useClientsQuery';
import { ClientsFilters, ClientTypeEnum } from '@/types/schema';

import { ClientSelectProps } from './types';

const useOptionsQuery = (filters: ClientsFilters & { searchText?: string }) => {
  const { fetchMore, clients } = useClientsQuery({
    filters: {
      clientIds: filters.clientIds,
      clientName: filters.searchText,
      status: filters.status,
      type: ClientTypeEnum.Project,
    },
  });

  return { data: clients, fetchMore };
};

const renderOption =
  (t: TFunction) => (option: ClientFilterFragment, state: AutocompleteRenderOptionState) => {
    const { selected } = state;

    const renderItem = () => (
      <Box
        key={option.id}
        className={cn(
          'mb-2 flex w-full items-center justify-between py-[1.1rem] px-12 rounded-8 hover:bg-bg-hover',
        )}>
        <Box>
          <Stack alignItems="center" gapX={8}>
            <Avatar src={option.clientIcon?.url || ''} />
            <Typography color="text-text-primary" variant="body-short-01">
              {option.clientName}
            </Typography>
          </Stack>
        </Box>
        <Checkbox key={option.id} checked={selected} size="medium" />
      </Box>
    );

    return renderItem();
  };

const renderNoOptionsText = (t: TFunction) => () => (
  <Typography color="text-text-placeholder" variant="body-short-01">
    {t('Common:NoResultsFound')}
  </Typography>
);

const renderTags = (disabled?: boolean) => (options: ClientFilterFragment[]) => (
  <Typography
    color={disabled ? 'text-text-placeholder' : 'text-text-primary'}
    variant="body-short-01">
    {options.map((option) => option.clientName).join(', ')}
  </Typography>
);

function ClientSelectSearch({ selectedClientIds, disabled, onChange }: ClientSelectProps) {
  const { t } = useTranslation();

  const { clients } = useClientsQuery({
    filters: { clientIds: selectedClientIds, type: ClientTypeEnum.Project },
    pause: !selectedClientIds?.length,
  });

  const onClientChange = useCallback(
    (clients: ClientFilterFragment[] | null) => {
      if (clients) {
        onChange(clients.map((client) => client.id));
      } else {
        onChange([]);
      }
    },
    [onChange],
  );

  return (
    <AsyncSelect
      disableClearable
      multiple
      classes={{ option: 'hover:bg-bg-primary' }}
      disabled={disabled}
      getOptionLabel={(option) => option.clientName}
      noOptionsText={renderNoOptionsText(t)}
      placeholder={selectedClientIds?.length ? '' : t('Common:Filter.SelectClients')}
      renderOption={renderOption(t)}
      renderTags={renderTags(disabled)}
      useOptionsQuery={useOptionsQuery}
      value={selectedClientIds?.length ? clients : []}
      onChange={onClientChange}
    />
  );
}

export default ClientSelectSearch;
